import React, { createContext, useContext, useEffect, useState } from 'react';

/**
 * Modo de visualización de la app (a nivel aplicación, toggle en el header).
 * - `oficina`: paleta "campo ledger" descansada + cards con colores pastel. Default.
 * - `noche`: theme oscuro (fondo oscuro cálido + texto claro + acentos brillantes).
 *
 * Se aplica vía `data-theme` en <html> y se persiste en localStorage.
 */
export type AppTheme = 'oficina' | 'noche';

const STORAGE_KEY = 'mcd_theme';
const THEME_ORDER: AppTheme[] = ['oficina', 'noche'];

export const THEME_LABELS: Record<AppTheme, string> = {
  oficina: 'Oficina',
  noche: 'Oscuro',
};

interface ThemeContextValue {
  theme: AppTheme;
  setTheme: (t: AppTheme) => void;
  cycle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function applyTheme(theme: AppTheme) {
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.theme = theme;
  }
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<AppTheme>(() => {
    if (typeof window === 'undefined') return 'oficina';
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      if (saved === 'oficina' || saved === 'noche') return saved;
      // Migración: si tenía 'colorido' guardado, pasar a 'oficina'.
      if (saved === 'colorido') return 'oficina';
    } catch {
      /* ignore */
    }
    return 'oficina';
  });

  useEffect(() => {
    applyTheme(theme);
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* ignore */
    }
  }, [theme]);

  const value: ThemeContextValue = {
    theme,
    setTheme: setThemeState,
    cycle: () =>
      setThemeState((t) => {
        const i = THEME_ORDER.indexOf(t);
        return THEME_ORDER[(i + 1) % THEME_ORDER.length];
      }),
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme debe usarse dentro de <ThemeProvider>');
  return ctx;
}
